import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, User, MapPin, Calendar as CalendarIcon,
  FileText, Receipt, ExternalLink, Trash2, Download, Users,
  Plus, XCircle, Play, CheckCircle, Clock, PauseCircle,
  Phone, Mail, Loader2, Image as ImageIcon, X, Star
} from 'lucide-react';

const STATUS_MAP = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-700' },
  on_hold: { label: 'On Hold', color: 'bg-gray-100 text-gray-700' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700' },
};

const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (typeof photo === 'string') return photo;
  return photo.url || photo.data || photo.dataUrl || photo.thumbnail_url || null;
};

const getPhotoLabel = (photo, fallback = '') => {
  if (typeof photo === 'string') return fallback;
  if (!photo) return fallback;
  return photo.label || photo.caption || photo.name || photo.filename || fallback;
};

const JobFieldNotesSection = ({ job, organization }) => {
  const [siteVisit, setSiteVisit] = useState(null);
  const [estimate, setEstimate] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      const svId = job?.site_visit_id || job?.from_site_visit_id;
      if (svId) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/site-visits/${svId}`);
          if (res.ok) setSiteVisit(await res.json());
        } catch {}
      }
      if (job?.estimate_id) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/estimates/${job.estimate_id}`);
          if (res.ok) setEstimate(await res.json());
        } catch {}
      }
    };
    if (job && organization?.id) fetchData();
  }, [job, organization?.id]);
  
  // Pull measurements from site visit (priority) then estimate
  const structured = siteVisit?.structured_measurements || estimate?.structured_measurements || [];
  const legacyMeas = siteVisit?.measurements || estimate?.measurements || null;
  const scopeOfWork = siteVisit?.scope_of_work || estimate?.scope_of_work || '';
  const siteConditions = siteVisit?.site_conditions || [];
  const customConditions = siteVisit?.custom_conditions || [];
  const clientExp = siteVisit?.client_expectations;
  const ce = typeof clientExp === 'object' && clientExp !== null ? clientExp : {};
  const wantsDone = ce.wantsDone || ce.what_they_want || '';
  const timeline = ce.timeline || '';
  const budget = ce.budgetRange || ce.budget_range || '';
  const specialReq = ce.specialRequests || ce.special_requests || siteVisit?.special_requests || '';
  const checklistItems = siteVisit?.checklist_items || [];
  
  const hasMeasurements = structured.length > 0 || (legacyMeas && Object.keys(legacyMeas).some(k => legacyMeas[k]));
  const hasChecklist = checklistItems.length > 0;
  const hasConditions = siteConditions.some(c => c.checked) || customConditions.length > 0;
  const hasExpectations = wantsDone || timeline || budget || specialReq;
  const hasScope = !!scopeOfWork;
  
  if (!hasMeasurements && !hasChecklist && !hasConditions && !hasExpectations && !hasScope) return null;
  
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> From Site Visit
        </h2>
        {siteVisit && (
          <Link to={`/site-visits/${siteVisit.id}`} className="text-xs text-primary hover:underline flex items-center gap-1">
            View Source <ExternalLink className="w-3 h-3" />
          </Link>
        )}
      </div>
      
      {hasScope && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Scope of Work</p>
          <p className="text-sm bg-blue-50 rounded-lg p-3 whitespace-pre-wrap">{scopeOfWork}</p>
        </div>
      )}
      
      {hasMeasurements && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Measurements</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {structured.map((m, i) => (
              <div key={i} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-gray-700">{m.label}:</span>{' '}
                <span className="text-gray-900">{m.value} {m.unit || ''}</span>
                {m.notes && <div className="text-xs text-gray-500 mt-0.5">{m.notes}</div>}
              </div>
            ))}
            {!structured.length && legacyMeas && Object.entries(legacyMeas).filter(([k,v]) => v).map(([k, v]) => (
              <div key={k} className="bg-gray-50 rounded-lg px-3 py-2 text-sm">
                <span className="font-medium text-gray-700 capitalize">{k.replace(/_/g, ' ')}:</span>{' '}
                <span className="text-gray-900">{String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {hasExpectations && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Customer Requirements</p>
          <div className="space-y-1.5 text-sm">
            {wantsDone && <div className="bg-amber-50 rounded-lg px-3 py-2"><span className="font-medium">What they want:</span> {wantsDone}</div>}
            {timeline && <div className="bg-amber-50 rounded-lg px-3 py-2"><span className="font-medium">Timeline:</span> {timeline}</div>}
            {budget && budget !== 'not_discussed' && <div className="bg-amber-50 rounded-lg px-3 py-2"><span className="font-medium">Budget:</span> {budget}</div>}
            {specialReq && <div className="bg-amber-50 rounded-lg px-3 py-2"><span className="font-medium">Special requests:</span> {specialReq}</div>}
          </div>
        </div>
      )}
      
      {hasConditions && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Site Conditions</p>
          <div className="flex flex-wrap gap-1.5">
            {siteConditions.filter(c => c.checked).map((c, i) => (
              <Badge key={i} variant="outline" className="bg-red-50 border-red-200 text-red-700 whitespace-normal text-left py-1">
                {c.name}{c.notes ? ` — ${c.notes}` : ''}
              </Badge>
            ))}
            {customConditions.map((c, i) => (
              <Badge key={`cc-${i}`} variant="outline" className="bg-red-50 border-red-200 text-red-700 whitespace-normal text-left py-1">
                {c.name || c}
              </Badge>
            ))}
          </div>
        </div>
      )}
      
      {hasChecklist && (
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Field Notes Checklist</p>
          <div className="space-y-1.5">
            {checklistItems.map((item, i) => (
              <div key={i} className="flex items-start gap-2 bg-gray-50 rounded-lg px-3 py-2">
                {item.completed || item.checked ? (
                  <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <div className="w-4 h-4 border-2 border-gray-300 rounded mt-0.5 flex-shrink-0" />
                )}
                <span className="text-sm whitespace-normal break-words leading-snug">
                  {item.label || item.text || item.name || (typeof item === 'string' ? item : 'Checklist item')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const JobDocumentsSection = ({ job, organization, jobId, onJobUpdate, user }) => {
  const [isUploading, setIsUploading] = useState(false);
  const documents = job.documents || [];
  
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setIsUploading(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/documents`);
        if (user?.name) url.searchParams.set('user_name', user.name);
        const res = await fetch(url.toString(), { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          onJobUpdate(prev => ({ ...prev, documents: [...(prev.documents || []), data.document] }));
          uploaded++;
        }
      } catch {}
    }
    setIsUploading(false);
    e.target.value = '';
    if (uploaded > 0) toast.success(`${uploaded} document${uploaded > 1 ? 's' : ''} uploaded`);
    else toast.error('Upload failed');
  };
  
  const handleDelete = async (docId) => {
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/documents/${docId}`, { method: 'DELETE' });
      onJobUpdate(prev => ({ ...prev, documents: (prev.documents || []).filter(d => d.id !== docId) }));
      toast.success('Document removed');
    } catch { toast.error('Failed to remove'); }
  };
  
  const fileIcon = (filename) => {
    const ext = (filename || '').split('.').pop()?.toLowerCase();
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['png', 'jpg', 'jpeg', 'webp', 'gif'].includes(ext)) return '🖼️';
    if (['zip', 'rar'].includes(ext)) return '🗜️';
    return '📎';
  };
  
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary" /> Documents {documents.length > 0 && <span className="text-sm text-muted-foreground font-normal">({documents.length})</span>}
        </h2>
        <label className="cursor-pointer">
          <input
            type="file"
            multiple
            className="hidden"
            onChange={handleUpload}
            disabled={isUploading}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.webp,.gif,.txt,.heic"
            data-testid="job-document-upload"
          />
          <span className={`text-sm font-medium flex items-center gap-1 px-2 py-1 rounded-lg ${isUploading ? 'text-gray-400' : 'text-primary hover:bg-primary/10'}`}>
            {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {isUploading ? 'Uploading…' : 'Upload'}
          </span>
        </label>
      </div>
      {documents.length === 0 ? (
        <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-xl">
          <FileText className="w-7 h-7 text-gray-300 mx-auto mb-1.5" />
          <p className="text-sm text-muted-foreground">No documents yet</p>
          <p className="text-xs text-muted-foreground mt-0.5">Upload contracts, receipts, specs, etc.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5 group">
              <span className="text-2xl flex-shrink-0">{fileIcon(doc.filename)}</span>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{doc.filename || 'Document'}</p>
                <p className="text-xs text-muted-foreground">
                  {doc.size ? `${(doc.size / 1024).toFixed(0)} KB · ` : ''}
                  {doc.uploaded_by ? `${doc.uploaded_by} · ` : ''}
                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : ''}
                </p>
              </div>
              <a
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline"
              >
                Open
              </a>
              <button
                onClick={() => handleDelete(doc.id)}
                className="text-red-500 hover:text-red-700 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JobPhotosSection = ({ job, organization, jobId, onJobUpdate, user }) => {
  const [linkedPhotos, setLinkedPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [photoType, setPhotoType] = useState('progress');
  
  // Load photos from linked estimate + site visit
  useEffect(() => {
    const loadLinked = async () => {
      const pool = [];
      // Site visit photos
      const siteVisitId = job.site_visit_id || job.from_site_visit_id;
      if (siteVisitId) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/site-visits/${siteVisitId}`);
          if (res.ok) {
            const sv = await res.json();
            (sv.photos || []).forEach(p => {
              const url = getPhotoUrl(p);
              if (url) pool.push({ url, label: getPhotoLabel(p, 'Site visit photo'), source: 'Site Visit', type: 'before' });
            });
          }
        } catch {}
      }
      // Estimate photos (damage/before photos)
      if (job.estimate_id) {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/estimates/${job.estimate_id}`);
          if (res.ok) {
            const est = await res.json();
            (est.photos || []).forEach(p => {
              const url = getPhotoUrl(p);
              if (url) pool.push({ url, label: getPhotoLabel(p, 'Estimate photo'), source: 'Estimate', type: 'before' });
            });
          }
        } catch {}
      }
      setLinkedPhotos(pool);
    };
    if (job && organization?.id) loadLinked();
  }, [job, organization?.id]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setIsUploading(true);
    let successCount = 0;
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/photos`);
        url.searchParams.set('photo_type', photoType);
        url.searchParams.set('label', file.name.replace(/\.[^.]+$/, ''));
        if (user?.id) url.searchParams.set('user_id', user.id);
        if (user?.name) url.searchParams.set('user_name', user.name);
        const res = await fetch(url.toString(), { method: 'POST', body: formData });
        if (res.ok) {
          const data = await res.json();
          onJobUpdate(prev => ({ ...prev, photos: [...(prev.photos || []), data.photo] }));
          successCount++;
        } else {
          const err = await res.json().catch(() => ({}));
          console.error('Upload failed:', err);
        }
      } catch (err) {
        console.error('Upload error:', err);
      }
    }
    setIsUploading(false);
    e.target.value = '';
    if (successCount > 0) {
      toast.success(`${successCount} photo${successCount > 1 ? 's' : ''} uploaded`);
    } else {
      toast.error('Upload failed. Please try again.');
    }
  };

  const handleRemove = async (photoId) => {
    if (!photoId) return;
    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/photos/${photoId}`,
        { method: 'DELETE' }
      );
      onJobUpdate(prev => ({ ...prev, photos: (prev.photos || []).filter(p => (typeof p === 'object' ? p.id : p) !== photoId) }));
      toast.success('Photo removed');
    } catch {
      toast.error('Failed to remove');
    }
  };

  // Normalize job photos
  const jobPhotos = (job.photos || []).map(p => {
    const url = getPhotoUrl(p);
    if (!url) return null;
    return {
      url,
      label: getPhotoLabel(p, 'Job photo'),
      source: 'Job',
      type: typeof p === 'object' ? (p.photo_type || 'general') : 'general',
      id: typeof p === 'object' ? p.id : null,
      uploaded_at: typeof p === 'object' ? p.uploaded_at : null,
      uploaded_by: typeof p === 'object' ? p.uploaded_by : null,
    };
  }).filter(Boolean);
  
  const allPhotos = [...jobPhotos, ...linkedPhotos];

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-primary" />
          Photos {allPhotos.length > 0 && <span className="text-sm text-muted-foreground font-normal">({allPhotos.length})</span>}
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={photoType}
            onChange={(e) => setPhotoType(e.target.value)}
            className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-primary"
            data-testid="photo-type-select"
          >
            <option value="before">Before</option>
            <option value="progress">Progress</option>
            <option value="after">After</option>
            <option value="damage">Damage</option>
            <option value="general">General</option>
          </select>
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={handleUpload}
              disabled={isUploading}
              data-testid="job-photo-upload"
            />
            <span className={`text-sm font-medium flex items-center gap-1 px-2 py-1 rounded-lg ${isUploading ? 'text-gray-400' : 'text-primary hover:bg-primary/10'}`}>
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {isUploading ? 'Uploading…' : 'Add'}
            </span>
          </label>
        </div>
      </div>
      
      {allPhotos.length > 0 ? (
        <div className="grid grid-cols-3 gap-2">
          {allPhotos.map((p, idx) => (
            <div key={`${p.source}-${p.id || idx}`} className="relative group">
              <img
                src={p.url}
                alt={p.label}
                className="rounded-lg border aspect-square object-cover w-full cursor-pointer transition-transform group-hover:scale-[1.02]"
                onClick={() => setLightbox(p)}
                data-testid={`job-photo-${idx}`}
              />
              <div className="absolute bottom-1 left-1 right-1 bg-black/65 text-white text-[10px] px-1.5 py-0.5 rounded truncate pointer-events-none">
                {p.source}{p.type && p.type !== 'general' ? ` · ${p.type}` : ''}
              </div>
              {p.source === 'Job' && p.id && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleRemove(p.id); }}
                  className="absolute top-1 right-1 bg-red-500 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  title="Remove"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
          <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No photos yet</p>
          <p className="text-xs text-muted-foreground mt-1">Photos from site visits and estimates will also appear here</p>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full w-10 h-10 flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img src={lightbox.url} alt={lightbox.label} className="max-h-[80vh] w-full object-contain rounded-xl" />
            <div className="mt-3 text-center text-white">
              <p className="font-medium">{lightbox.label}</p>
              <p className="text-xs opacity-75 mt-1">
                From: {lightbox.source}
                {lightbox.type && lightbox.type !== 'general' ? ` · ${lightbox.type}` : ''}
                {lightbox.uploaded_by ? ` · by ${lightbox.uploaded_by}` : ''}
                {lightbox.uploaded_at ? ` · ${new Date(lightbox.uploaded_at).toLocaleDateString()}` : ''}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const JobDetailPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  
  const [job, setJob] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [invoiceExists, setInvoiceExists] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedDuty, setSelectedDuty] = useState('crew');

  const primaryColor = organization?.branding?.primary_color || organization?.brand_primary_color || '#3b82f6';

  useEffect(() => {
    if (!organization?.id || !jobId) return;
    
    const load = async () => {
      try {
        const res = await apiHelpers.getJob(organization.id, jobId);
        setJob(res.data);
        // Check if linked invoice still exists
        const invId = res.data.invoice_id;
        if (invId) {
          try {
            await apiHelpers.getInvoice(organization.id, invId);
            setInvoiceExists(true);
          } catch { setInvoiceExists(false); }
        }
        // Load employees for assignment
        try {
          const empResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/employees`
          );
          if (empResponse.ok) {
            const empData = await empResponse.json();
            setEmployees(Array.isArray(empData) ? empData : (empData.employees || []));
          }
        } catch {}
      } catch (err) {
        console.error('Job load error:', err);
        toast.error('Failed to load job');
        navigate('/jobs');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [organization?.id, jobId, navigate]);

  const handleAddStaff = async () => {
    if (!selectedEmployeeId) {
      toast.error('Pick an employee first');
      return;
    }
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/staff`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ employee_id: selectedEmployeeId, duty: selectedDuty })
        }
      );
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to assign staff');
      }
      const updatedJob = await response.json();
      setJob(updatedJob);
      setShowAddStaff(false);
      setSelectedEmployeeId('');
      setSelectedDuty('crew');
      toast.success('Staff assigned');
    } catch (e) {
      toast.error(e.message || 'Failed to assign staff');
    }
  };

  const handleRemoveStaff = async (employeeId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/staff/${employeeId}`,
        { method: 'DELETE' }
      );
      if (response.ok) {
        const updatedJob = await response.json();
        setJob(updatedJob);
        toast.success('Staff removed');
      }
    } catch (e) {
      toast.error('Failed to remove staff');
    }
  };

  const handleUpdateStatus = async (newStatus) => {
    try {
      const res = await apiHelpers.updateJob(organization.id, jobId, { status: newStatus }, user.id, user.name);
      setJob(res.data);
      toast.success(`Status updated to ${newStatus.replace('_', ' ')}`);
      
      if (newStatus === 'completed') {
        handleCreateInvoice();
      }
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleCreateInvoice = async () => {
    const estId = job?.estimate_id || job?.linked_estimate_id;
    if (!estId) {
      toast.error('No linked estimate');
      return;
    }
    try {
      const estRes = await apiHelpers.getEstimate(organization.id, estId);
      const est = estRes.data;
      const invoiceData = {
        client_id: job.client_id,
        estimate_id: estId,
        job_id: job.id,
        amount_due: est.total || 0,
        line_items: est.line_items || [],
        display_mode: est.display_mode || 'detailed',
        description: `Invoice for ${job.title || 'Job'}`,
        due_date: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
      };
      const res = await apiHelpers.createInvoice(organization.id, invoiceData, user.id, user.name);
      await apiHelpers.updateJob(organization.id, job.id, { invoice_id: res.data.id }, user.id, user.name);
      toast.success('Invoice created!');
      navigate(`/invoices/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invoice');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this job? This cannot be undone.')) return;
    try {
      await apiHelpers.deleteJob(organization.id, jobId, user.id);
      toast.success('Job deleted');
      navigate('/jobs');
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!job) return null;

  const status = STATUS_MAP[job.status] || STATUS_MAP.scheduled;
  const jobAddress = job.address || job.job_address || '';
  const jobStartDate = job.start_date || job.scheduled_date;
  const jobEstimateId = job.estimate_id || job.linked_estimate_id;
  const jobInvoiceId = job.invoice_id;

  return (
    <div className="space-y-6 pb-24" data-testid="job-detail-page">
      {/* Header */}
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} className="mb-2 -ml-2 text-muted-foreground">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Jobs
        </Button>
        <h1 className="text-2xl sm:text-3xl font-bold">{job.title || 'Job'}</h1>
        <div className="flex items-center gap-2 mt-2">
          <Badge className={`${status.color} text-sm px-3 py-1`}>{status.label}</Badge>
          <span className="text-sm text-muted-foreground">{job.job_type || job.type || ''}</span>
        </div>
      </div>

      {/* Needs Scheduling banner — auto-created from deposit-paid */}
      {(job.needs_scheduling || (!job.start_date && !job.scheduled_date)) && job.status !== 'completed' && job.status !== 'cancelled' && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-4" data-testid="needs-scheduling-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900">
                {job.auto_created_reason === 'deposit_paid'
                  ? 'Deposit paid — set a start date'
                  : 'This job needs to be scheduled'}
              </p>
              <p className="text-sm text-amber-800 mt-0.5">
                {job.auto_created_reason === 'deposit_paid'
                  ? 'The client has paid their deposit. Pick a start date and assign your crew below.'
                  : 'Add a start date to put this job on the calendar.'}
              </p>
            </div>
          </div>
          <div className="mt-3 flex flex-col sm:flex-row gap-2">
            <input
              type="date"
              className="flex-1 border border-amber-300 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:border-primary"
              data-testid="needs-scheduling-date-input"
              onChange={async (e) => {
                if (!e.target.value) return;
                try {
                  await apiHelpers.updateJob(organization.id, jobId, {
                    start_date: e.target.value,
                    needs_scheduling: false,
                  });
                  const res = await apiHelpers.getJob(organization.id, jobId);
                  setJob(res.data);
                  toast.success('Start date saved');
                } catch (err) {
                  toast.error('Failed to save date');
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Status Controls */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-sm text-muted-foreground mb-3">Update Status</p>
        <div className="flex gap-2 flex-wrap">
          {Object.entries(STATUS_MAP).map(([value, cfg]) => (
            <button
              key={value}
              onClick={() => handleUpdateStatus(value)}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium border-2 transition-all ${
                job.status === value
                  ? cfg.color + ' ring-2 ring-offset-2 border-transparent'
                  : 'bg-gray-50 text-gray-600 hover:bg-gray-100 border-transparent'
              }`}
              data-testid={`job-status-${value}`}
            >
              {cfg.label}
            </button>
          ))}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-4">
          {/* Client */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <User className="w-5 h-5" style={{ color: primaryColor }} /> Customer
            </h2>
            <p className="font-semibold text-lg">{job.client?.name || 'Unknown Customer'}</p>
            {job.client?.phone && (
              <a href={`tel:${job.client.phone}`} className="flex items-center gap-2 text-sm text-muted-foreground mt-1 hover:text-primary">
                <Phone className="w-3.5 h-3.5" /> {job.client.phone}
              </a>
            )}
            {job.client?.email && (
              <a href={`mailto:${job.client.email}`} className="flex items-center gap-2 text-sm text-muted-foreground mt-1 hover:text-primary">
                <Mail className="w-3.5 h-3.5" /> {job.client.email}
              </a>
            )}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">{jobAddress || 'No address'}</p>
                  {jobAddress && (
                    <a href={`https://maps.google.com?q=${encodeURIComponent(jobAddress)}`} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                      Open in Maps
                    </a>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Scheduled for</p>
                <p className="font-medium">
                  {jobStartDate ? new Date(jobStartDate).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'Not yet scheduled'}
                </p>
              </div>
            </div>
          </div>

          {/* Staff */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold flex items-center gap-2">
                <Users className="w-5 h-5" style={{ color: primaryColor }} /> Assigned Staff
              </h2>
              {!showAddStaff && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddStaff(true)}
                  className="h-8 text-xs"
                  data-testid="add-staff-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Assign
                </Button>
              )}
            </div>
            {(!job.staff_assignments || job.staff_assignments.length === 0) ? (
              <p className="text-sm text-muted-foreground text-center py-2">No staff assigned yet</p>
            ) : (
              <div className="space-y-2">
                {job.staff_assignments.map((a, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-xl px-3 py-2.5">
                    <div>
                      <p className="font-medium text-sm">{a.employee_name}</p>
                      <Badge variant="outline" className="text-[10px] mt-0.5">{a.duty}</Badge>
                    </div>
                    <button
                      onClick={() => handleRemoveStaff(a.employee_id)}
                      className="text-red-500 hover:text-red-700 p-1"
                      data-testid={`remove-staff-${a.employee_id}`}
                      title="Remove from job"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {showAddStaff && (
              <div className="mt-3 space-y-2 border-t pt-3">
                {employees.length === 0 ? (
                  <p className="text-xs text-muted-foreground">
                    No employees yet. Add staff under Settings &rarr; Team first.
                  </p>
                ) : (
                  <>
                    <select
                      value={selectedEmployeeId}
                      onChange={(e) => setSelectedEmployeeId(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary"
                      data-testid="staff-employee-select"
                    >
                      <option value="">Pick a team member…</option>
                      {employees
                        .filter(e => !(job.staff_assignments || []).some(a => a.employee_id === e.id))
                        .map(e => (
                          <option key={e.id} value={e.id}>
                            {e.first_name} {e.last_name}
                            {e.role ? ` — ${e.role}` : ''}
                          </option>
                        ))}
                    </select>
                    <select
                      value={selectedDuty}
                      onChange={(e) => setSelectedDuty(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary"
                      data-testid="staff-duty-select"
                    >
                      <option value="crew">Crew</option>
                      <option value="lead">Crew Lead</option>
                      <option value="supervisor">Supervisor</option>
                      <option value="apprentice">Apprentice</option>
                    </select>
                  </>
                )}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => { setShowAddStaff(false); setSelectedEmployeeId(''); }}
                    className="flex-1"
                  >Cancel</Button>
                  <Button
                    size="sm"
                    onClick={handleAddStaff}
                    disabled={!selectedEmployeeId}
                    className="flex-1"
                    data-testid="confirm-add-staff-btn"
                  >Assign</Button>
                </div>
              </div>
            )}
          </div>

          {/* Linked Documents */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-semibold mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: primaryColor }} /> Linked Documents
            </h2>
            <div className="space-y-2">
              {jobEstimateId && (
                <button onClick={() => navigate(`/estimates/${jobEstimateId}`)} className="flex items-center gap-2 w-full p-3 bg-blue-50 rounded-xl text-blue-700 hover:bg-blue-100 transition-colors text-left">
                  <FileText className="w-4 h-4" /> View Estimate <ExternalLink className="w-3 h-3 ml-auto" />
                </button>
              )}
              {jobInvoiceId && invoiceExists && (
                <button onClick={() => navigate(`/invoices/${jobInvoiceId}`)} className="flex items-center gap-2 w-full p-3 bg-green-50 rounded-xl text-green-700 hover:bg-green-100 transition-colors text-left">
                  <Receipt className="w-4 h-4" /> View Invoice <ExternalLink className="w-3 h-3 ml-auto" />
                </button>
              )}
              {(!jobInvoiceId || !invoiceExists) && job.status === 'completed' && (
                <Button onClick={handleCreateInvoice} className="w-full bg-green-600 hover:bg-green-700 text-white rounded-xl h-11">
                  <Receipt className="w-4 h-4 mr-2" /> Create Invoice
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Add Progress Notes */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <h2 className="font-semibold mb-3">Progress Notes</h2>
            <textarea
              className="w-full border rounded-xl p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-200"
              rows={3}
              placeholder="Add a progress note..."
              onKeyDown={async (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  const note = e.target.value.trim();
                  if (!note) return;
                  const existingNotes = job.internal_notes || '';
                  const timestamp = new Date().toLocaleString();
                  const updated = `${existingNotes}\n\n[${timestamp}] ${note}`;
                  try {
                    await apiHelpers.updateJob(organization.id, jobId, { internal_notes: updated }, user.id, user.name);
                    setJob(prev => ({ ...prev, internal_notes: updated }));
                    e.target.value = '';
                    toast.success('Note added');
                  } catch { toast.error('Failed to add note'); }
                }
              }}
              data-testid="job-add-note"
            />
            <p className="text-xs text-muted-foreground mt-1">Press Enter to save note</p>
          </div>

          {/* Field Notes from Site Visit (measurements, checklist, expectations) */}
          <JobFieldNotesSection job={job} organization={organization} />

          {/* Internal Notes / Field Data */}
          {job.internal_notes && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-semibold mb-3">Field Notes & History</h2>
              <div className="text-sm whitespace-pre-wrap bg-gray-50 rounded-xl p-4 max-h-[400px] overflow-y-auto leading-relaxed">
                {job.internal_notes}
              </div>
            </div>
          )}
          
          {/* Documents (contracts, receipts, specs, etc.) */}
          <JobDocumentsSection
            job={job}
            organization={organization}
            jobId={jobId}
            onJobUpdate={setJob}
            user={user}
          />

          {/* Photos — with add button */}
          {/* Photos — Unified pool from job + estimate + site visit */}
          <JobPhotosSection
            job={job}
            organization={organization}
            jobId={jobId}
            onJobUpdate={setJob}
            user={user}
          />

          {/* Sketches */}
          {job.sketches && job.sketches.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-semibold mb-3">Sketches</h2>
              <div className="grid grid-cols-2 gap-2">
                {job.sketches.map((sketch, idx) => (
                  <img
                    key={idx}
                    src={typeof sketch === 'string' ? sketch : (sketch.url || sketch.data || sketch.dataUrl)}
                    alt={`Sketch ${idx + 1}`}
                    className="rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                    onClick={() => {
                      const src = typeof sketch === 'string' ? sketch : (sketch.url || sketch.data || sketch.dataUrl);
                      window.open(src, '_blank');
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 space-y-2">
            <h2 className="font-semibold mb-3">Actions</h2>
            <Button variant="outline" className="w-full justify-start rounded-xl" onClick={() => {
              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/organizations/${organization.id}/jobs/${jobId}/pdf`, '_blank');
            }}>
              <Download className="w-4 h-4 mr-2" /> Download Job Sheet (PDF)
            </Button>
            {job.status === 'completed' && job.public_review_token && (
              <Button
                variant="outline"
                className="w-full justify-start rounded-xl"
                onClick={() => {
                  const url = `${window.location.origin}/job-status/${job.public_review_token}`;
                  navigator.clipboard?.writeText(url);
                  window.open(url, '_blank');
                  toast.success('Customer page opened (link copied to clipboard)');
                }}
                data-testid="view-customer-page-btn"
              >
                <Star className="w-4 h-4 mr-2" /> View Customer Page
                {job.review_rating && (
                  <span className="ml-auto text-amber-500 text-sm">
                    {'★'.repeat(job.review_rating)}
                  </span>
                )}
              </Button>
            )}
            {job.status === 'completed' && (
              <Button variant="outline" className="w-full justify-start rounded-xl" onClick={async () => {
                try {
                  await apiHelpers.updateJob(organization.id, jobId, { status: 'archived' }, user.id, user.name);
                  toast.success('Job archived');
                  navigate('/jobs');
                } catch { toast.error('Failed to archive'); }
              }}>
                <XCircle className="w-4 h-4 mr-2" /> Archive Job
              </Button>
            )}
            <Button variant="outline" className="w-full justify-start text-red-600 hover:bg-red-50 rounded-xl" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" /> Delete Job
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetailPage;
